/**
 * emailReputationMockAdapter
 *
 * P3 Checkpoint 3.5D shell-phase mock data source for the Email
 * "Reputation" view.
 *
 * Product direction for this checkpoint (explicit, distinct from 3.5A/3.5B/
 * 3.5C): design the operator experience now, on deterministic mocks, and
 * clearly RECORD what real integration each section still needs — rather
 * than deferring the page because the data isn't live yet. Every field
 * below is therefore tagged with a `dataAvailability` value:
 *
 *   "derivable_not_exposed" — the underlying SendGrid event vocabulary is
 *     real and verified in the repository (see backend/app/integrations/
 *     sendgrid/{enums,classifier,normalizer}.py), but no aggregation
 *     endpoint exists yet. Section A/F below.
 *   "pending_integration" — MissionAssist has NO current integration of
 *     any kind with this data source (no outbound SendGrid API client, no
 *     Google/Yahoo/Microsoft postmaster integration, no IP inventory, no
 *     domain-authentication verification). Sections B/C/D/E below.
 *
 * These are FRONTEND MOCK METADATA ONLY — not a backend enum, not a
 * contract MissionAssist's API exposes today. Do not treat them as
 * evidence of a real classification system; they exist purely so this
 * file (and, eventually, a cross-Dashboard data-readiness audit) can
 * track what each section still needs without re-deriving it from scratch.
 *
 * Repository evidence this file is grounded in (read in full during this
 * checkpoint's characterization pass, Checkpoint 3.5C):
 *   - SendGridProviderEventType (enums.py): delivered/bounce/dropped/
 *     deferred/spamreport/unsubscribe are real, recognized event types.
 *   - classifier.py: bounce/drop classification uses free-text pattern
 *     matching against individual event evidence — never a structured
 *     SPF/DKIM/DMARC status. This file does NOT infer authentication
 *     health from that mechanism (see AUTHENTICATION below).
 *   - normalizer.py: `sending_ip` is captured per event; no IP inventory,
 *     dedicated/shared classification, or warm-up tracking exists
 *     anywhere in config.py or elsewhere.
 *   - No outbound SendGrid API client exists in the codebase (grepped for
 *     api.sendgrid.com / SendGridApiClient — zero results) — so SendGrid's
 *     own reputation score, blocklist monitoring, and inbox-placement
 *     testing are categorically unreachable today, not merely unexposed.
 *   - No Google Postmaster Tools / Yahoo-family / Microsoft
 *     sender-reputation integration exists anywhere in the repository.
 */

const HYGIENE_EVENT_KEYS = ["delivered", "bounced", "deferred"];

// Deterministic, hand-authored per-environment event-count pools — same
// "one canonical source, derive everything else" discipline as
// emailDeliverabilityMockAdapter.js. `attempted` is enforced (below) to
// equal delivered+bounced+deferred exactly, so every rate is provably
// derived from counts, never an independently hardcoded percentage.
// complaints/unsubscribes are modeled as a separate, smaller subset of
// delivered recipients who took a post-delivery action, not part of the
// attempted-outcome partition.
const QUALIFICATION_POOL = Object.freeze({
  delivered: 1120,
  bounced: 55,
  deferred: 25,
  complaints: 3,
  unsubscribes: 8,
});

const PRODUCTION_POOL = Object.freeze({
  delivered: 17800,
  bounced: 612,
  deferred: 380,
  complaints: 34,
  unsubscribes: 145,
});

const ENVIRONMENT_POOLS = Object.freeze({
  qualification: QUALIFICATION_POOL,
  production: PRODUCTION_POOL,
});

function attempted(pool) {
  return HYGIENE_EVENT_KEYS.reduce((total, key) => total + pool[key], 0);
}

// Module-load invariant — fails loudly at import time rather than
// silently drifting, matching this codebase's established pattern
// (emailPerformanceMockAdapter.js's assertBroadcastIdsUnique,
// emailDeliverabilityMockAdapter.js's assertPoolInvariants).
(function assertPoolInvariants() {
  for (const [env, pool] of Object.entries(ENVIRONMENT_POOLS)) {
    if (attempted(pool) <= 0) {
      throw new Error(`emailReputationMockAdapter: "${env}" attempted volume must be positive.`);
    }
    if (pool.complaints > pool.delivered || pool.unsubscribes > pool.delivered) {
      throw new Error(
        `emailReputationMockAdapter: "${env}" complaints/unsubscribes cannot exceed delivered (both are post-delivery subsets).`
      );
    }
  }
})();

function combinePools(pools) {
  const combined = { delivered: 0, bounced: 0, deferred: 0, complaints: 0, unsubscribes: 0 };
  for (const pool of pools) {
    combined.delivered += pool.delivered;
    combined.bounced += pool.bounced;
    combined.deferred += pool.deferred;
    combined.complaints += pool.complaints;
    combined.unsubscribes += pool.unsubscribes;
  }
  return combined;
}

function poolFor(environment) {
  if (environment === "all") return combinePools(Object.values(ENVIRONMENT_POOLS));
  const pool = ENVIRONMENT_POOLS[environment];
  if (!pool) throw new Error(`emailReputationMockAdapter: unknown environment "${environment}"`);
  return pool;
}

// Every rate is computed here, once, from the pool's own counts — never
// stored as an independent literal. deliveryRate/bounceRate/deferredRate
// are per-attempted; complaintRate/unsubscribeRate are per-delivered
// (industry convention: both are actions taken by someone who already
// received the message).
function buildSummary(pool) {
  const attemptedCount = attempted(pool);
  return {
    dataAvailability: "derivable_not_exposed",
    attempted: attemptedCount,
    delivered: pool.delivered,
    bounced: pool.bounced,
    deferred: pool.deferred,
    complaints: pool.complaints,
    unsubscribes: pool.unsubscribes,
    deliveryRate: attemptedCount ? pool.delivered / attemptedCount : 0,
    bounceRate: attemptedCount ? pool.bounced / attemptedCount : 0,
    deferredRate: attemptedCount ? pool.deferred / attemptedCount : 0,
    complaintRate: pool.delivered ? pool.complaints / pool.delivered : 0,
    unsubscribeRate: pool.delivered ? pool.unsubscribes / pool.delivered : 0,
  };
}

// Section B — Mailbox Provider Reputation. No Google Postmaster / Yahoo /
// Microsoft integration exists anywhere in the repository (see this
// file's header) — every entry is `pending_integration`, and its numbers
// are illustrative preview values, not derived from any real signal.
const MAILBOX_PROVIDERS = Object.freeze([
  Object.freeze({
    id: "google",
    label: "Google / Gmail",
    dataAvailability: "pending_integration",
    futureSource: "Google Postmaster Tools",
    previewDeliveryRate: 0.96,
    previewBounceRate: 0.02,
    previewComplaintIndicator: "Low",
  }),
  Object.freeze({
    id: "yahoo",
    label: "Yahoo / AOL",
    dataAvailability: "pending_integration",
    futureSource: "Yahoo-family sender reputation integration",
    previewDeliveryRate: 0.93,
    previewBounceRate: 0.035,
    previewComplaintIndicator: "Moderate",
  }),
  Object.freeze({
    id: "microsoft",
    label: "Microsoft / Outlook / Hotmail",
    dataAvailability: "pending_integration",
    futureSource: "Microsoft sender reputation integration",
    previewDeliveryRate: 0.91,
    previewBounceRate: 0.04,
    previewComplaintIndicator: "Moderate",
  }),
]);

// Section C — Sending IP Health. `sending_ip` itself is a real, captured
// per-event field (normalizer.py) — but there is no IP inventory,
// dedicated/shared classification, per-IP aggregation, or warm-up
// tracking anywhere in the backend, so every row here is illustrative.
// IPs are drawn from RFC 5737 documentation ranges (198.51.100.0/24,
// 203.0.113.0/24) — reserved for examples, never real routable addresses.
const SENDING_IPS = Object.freeze([
  Object.freeze({
    ip: "198.51.100.10",
    environment: "production",
    dataAvailability: "pending_integration",
    ipTypeLabel: "Dedicated (preview)",
    previewDeliveryRate: 0.95,
    previewBounceRate: 0.03,
    previewDeferredRate: 0.015,
  }),
  Object.freeze({
    ip: "198.51.100.11",
    environment: "production",
    dataAvailability: "pending_integration",
    ipTypeLabel: "Dedicated (preview)",
    previewDeliveryRate: 0.94,
    previewBounceRate: 0.032,
    previewDeferredRate: 0.018,
  }),
  Object.freeze({
    ip: "203.0.113.5",
    environment: "qualification",
    dataAvailability: "pending_integration",
    ipTypeLabel: "Shared (preview)",
    previewDeliveryRate: 0.92,
    previewBounceRate: 0.045,
    previewDeferredRate: 0.02,
  }),
]);

// Section D — Domain / Authentication Health. Deliberately NOT inferred
// from classifier.py's free-text bounce-reason pattern matching (that
// mechanism only labels one bounce's evidence text — it is not a
// structured, domain-level authentication status, and using it here would
// misrepresent an incidental heuristic as verified fact).
const AUTHENTICATION = Object.freeze({
  spf: Object.freeze({ id: "spf", label: "SPF", dataAvailability: "pending_integration" }),
  dkim: Object.freeze({ id: "dkim", label: "DKIM", dataAvailability: "pending_integration" }),
  dmarc: Object.freeze({ id: "dmarc", label: "DMARC", dataAvailability: "pending_integration" }),
  senderDomain: Object.freeze({
    id: "sender-domain",
    label: "Authenticated Sender Domain",
    dataAvailability: "pending_integration",
  }),
  marketingDomain: Object.freeze({
    id: "marketing-domain",
    label: "Marketing Domain",
    dataAvailability: "pending_integration",
  }),
});

// Section E — Blacklist / Inbox Placement. MissionAssist has never called
// a blocklist-monitoring or seed-list/inbox-placement provider — this
// section exists to establish the future wiring point, not to imply any
// current signal exists.
const BLACKLIST_INBOX_PLACEMENT = Object.freeze({
  blocklistStatus: Object.freeze({ dataAvailability: "pending_integration", futureSource: "Blocklist monitoring provider" }),
  inboxPlacement: Object.freeze({ dataAvailability: "pending_integration", futureSource: "Seed-list / inbox-placement provider" }),
  spamFolderPlacement: Object.freeze({ dataAvailability: "pending_integration", futureSource: "Seed-list / inbox-placement provider" }),
  lastChecked: null,
});

// Internal data-readiness metadata (Checkpoint 3.5D §6) — not necessarily
// rendered everywhere; exists so a future cross-Dashboard audit can
// consolidate every page's gaps without re-deriving them. Frontend-only,
// not a backend contract.
export const REPUTATION_DATA_READINESS = Object.freeze([
  { metric: "Delivery Rate", currentFrontend: "mock", currentBackend: "derivable_not_exposed", futureSource: "SendGrid event aggregation (new endpoint)" },
  { metric: "Bounce Rate", currentFrontend: "mock", currentBackend: "derivable_not_exposed", futureSource: "SendGrid event aggregation (new endpoint)" },
  { metric: "Deferred Rate", currentFrontend: "mock", currentBackend: "derivable_not_exposed", futureSource: "SendGrid event aggregation (new endpoint)" },
  { metric: "Spam Complaint Rate", currentFrontend: "mock", currentBackend: "derivable_not_exposed", futureSource: "SendGrid event aggregation (new endpoint)" },
  { metric: "Unsubscribe Rate", currentFrontend: "mock", currentBackend: "derivable_not_exposed", futureSource: "SendGrid event aggregation (new endpoint)" },
  { metric: "Google / Gmail reputation", currentFrontend: "mock/preview", currentBackend: "not_currently_available", futureSource: "Google Postmaster Tools" },
  { metric: "Yahoo / AOL reputation", currentFrontend: "mock/preview", currentBackend: "not_currently_available", futureSource: "Yahoo-family sender reputation integration" },
  { metric: "Microsoft reputation", currentFrontend: "mock/preview", currentBackend: "not_currently_available", futureSource: "Microsoft sender reputation integration" },
  { metric: "Sending IP aggregation", currentFrontend: "mock/preview", currentBackend: "derivable_not_exposed", futureSource: "New aggregation over sending_ip (real field, unaggregated)" },
  { metric: "Dedicated / Shared IP", currentFrontend: "mock/preview", currentBackend: "not_currently_available", futureSource: "SendGrid account / IP inventory (new concept)" },
  { metric: "IP Warm-up", currentFrontend: "mock/preview", currentBackend: "not_currently_available", futureSource: "SendGrid IP warm-up / config state (new concept)" },
  { metric: "SPF", currentFrontend: "pending", currentBackend: "not_currently_available", futureSource: "Domain/authentication verification integration" },
  { metric: "DKIM", currentFrontend: "pending", currentBackend: "not_currently_available", futureSource: "Domain/authentication verification integration" },
  { metric: "DMARC", currentFrontend: "pending", currentBackend: "not_currently_available", futureSource: "Domain/authentication verification integration" },
  { metric: "Blacklist / Blocklist", currentFrontend: "pending", currentBackend: "not_currently_available", futureSource: "Blocklist monitoring provider" },
  { metric: "Inbox Placement", currentFrontend: "pending", currentBackend: "not_currently_available", futureSource: "Seed-list / inbox-placement provider" },
]);

/**
 * getMockEmailReputationData
 *
 * @param {Object} params
 * @param {'all'|'qualification'|'production'} [params.environment]
 * @returns {{
 *   environment: string,
 *   summary: object,
 *   mailboxProviders: Array,
 *   sendingIps: Array,
 *   authentication: object,
 *   blacklistInboxPlacement: object,
 *   environmentComparison: Array<{environment:string, summary:object}>,
 * }}
 *
 * mailboxProviders/authentication/blacklistInboxPlacement are NOT
 * environment-scoped (no per-environment provider/authentication signal
 * exists to scope them by) — they render identically regardless of the
 * applied Environment filter. sendingIps IS filtered by environment when
 * a specific one is applied (mirroring how a real IP inventory would be
 * environment-scoped), and always includes both when "all" is applied.
 * environmentComparison always reports BOTH environments' summaries,
 * regardless of the applied filter — same "always cross-environment
 * context" pattern as emailDeliverabilityMockAdapter.js's
 * environmentBreakdown.
 */
export function getMockEmailReputationData({ environment = "all" } = {}) {
  const pool = poolFor(environment);

  return {
    environment,
    summary: buildSummary(pool),
    mailboxProviders: MAILBOX_PROVIDERS,
    sendingIps:
      environment === "all" ? SENDING_IPS : SENDING_IPS.filter((row) => row.environment === environment),
    authentication: AUTHENTICATION,
    blacklistInboxPlacement: BLACKLIST_INBOX_PLACEMENT,
    environmentComparison: Object.entries(ENVIRONMENT_POOLS).map(([env, p]) => ({
      environment: env,
      summary: buildSummary(p),
    })),
  };
}

export default getMockEmailReputationData;
